import { describeMaterialError } from "./lineErrors";
import { describeRateConfirmation } from "./traceModel";
import {
  NEGATIVE_LABOR_OVERRIDE_WARNING,
  ZERO_LABOR_TRACE_NOTE,
  ZERO_LABOR_TRACE_WARNING,
  describeQuotedPriceBasis,
  hasNegativeLaborOverride,
  hasZeroLaborOverride,
} from "./traceLabels";

const has = (value) => value !== null && value !== undefined && value !== "";

function compact(values) {
  return values.filter(Boolean);
}

function childLabel(child, index) {
  return (
    String(child?.name || "").trim() ||
    child?.product_type_display_name ||
    `Component ${index + 1}`
  );
}

function rateCardSource(child) {
  const card = child?.rate_card_resolved;
  if (!card || card.labor_card_missing) return null;
  const identity = compact([
    card.category,
    card.id != null ? `card #${card.id}` : null,
    card.band_label,
  ]).join(" · ");
  return {
    label: "Rate card",
    value: identity || "Matched",
    hint: describeRateConfirmation(card.rate_confirmed_at),
  };
}

/** A quoted price on a component says which unit it was quoted in. */
function quotedBasisSource(child) {
  if (child?.material_price_override?.applied !== true) return null;
  const basis = describeQuotedPriceBasis(child.material_price_override.basis);
  if (!basis) return null;
  return { label: "Quoted basis", value: basis };
}

function materialSource(child) {
  const resolution = child?.material_resolution;
  const name =
    resolution?.material_name ||
    child?.material_name_used ||
    resolution?.material_class;
  if (!name && !resolution) return null;
  return {
    label: "Material record",
    value: name || "Not resolved",
  };
}

function buildChildNode(child, fallbackIndex) {
  const index = child?.child_index ?? fallbackIndex;
  const materialError = child?.material_error
    ? describeMaterialError(child.material_error).message
    : null;
  const missingCard = Boolean(child?.rate_card_resolved?.labor_card_missing);
  const inputData = child?.input_data;
  const zeroLabor = hasZeroLaborOverride(inputData);
  const hasNegativeLabor = hasNegativeLaborOverride(inputData);
  const zeroLaborConfirmed = Boolean(inputData?.zero_labor_confirmed);

  return {
    id: `child-${index}`,
    label: childLabel(child, fallbackIndex),
    value: child?.material_cost_line_total,
    valueKind: "currency",
    rows: compact([
      has(child?.material_cost_per_unit)
        ? {
            label: "Per piece material",
            value: child.material_cost_per_unit,
            valueKind: "currency",
          }
        : null,
      has(child?.quantity_per_parent)
        ? {
            label: "Quantity per parent",
            value: child.quantity_per_parent,
            valueKind: "number",
          }
        : null,
      has(child?.labor_estimate_hours)
        ? {
            label: "Hours per piece",
            value: child.labor_estimate_hours,
            valueKind: "hours",
          }
        : null,
      has(child?.calculated_sqft)
        ? {
            label: "Area each",
            value: child.calculated_sqft,
            valueKind: "area",
          }
        : null,
    ]),
    source: compact([
      rateCardSource(child),
      materialSource(child),
      quotedBasisSource(child),
    ]),
    badges:
      child?.material_price_override?.applied === true
        ? [{ text: "Quoted price", tone: "quoted" }]
        : [],
    warnings: compact([
      hasNegativeLabor
        ? { text: NEGATIVE_LABOR_OVERRIDE_WARNING, tone: "error" }
        : null,
      zeroLabor && !zeroLaborConfirmed
        ? { text: ZERO_LABOR_TRACE_WARNING, tone: "error" }
        : null,
      materialError ? { text: materialError, tone: "error" } : null,
      child?.child_error
        ? { text: String(child.child_error), tone: "error" }
        : null,
      missingCard
        ? { text: "No rate card matches this component", tone: "warn" }
        : null,
    ]),
    note:
      zeroLabor && zeroLaborConfirmed && !hasNegativeLabor
        ? ZERO_LABOR_TRACE_NOTE
        : missingCard
          ? "This component's family has no rate card — a quoted price entered here is filed as a candidate for review."
          : undefined,
  };
}

export function buildChildComponentsNode(item, finishRatesById) {
  const children = Array.isArray(item?.children) ? item.children : [];
  if (children.length === 0) return null;

  // Reserved for finish provenance as the backend contract grows; accepting
  // the map keeps this seam aligned with the rest of traceModel today.
  void finishRatesById;

  return {
    id: "children",
    label: "Child components",
    value: item?.children_material_cost_per_unit,
    valueKind: "currency",
    valueSuffix: "material per piece",
    tone: "material",
    note: "Each component prices as its own product type and rolls into this line. Its hours are already in Shop labor above.",
    rows: [
      { label: "Components", value: children.length, valueKind: "number" },
      {
        label: "Component hours per piece",
        value: item?.children_labor_hours_per_unit,
        valueKind: "hours",
      },
    ],
    children: children.map(buildChildNode),
  };
}
